import clamp from '../../../_utils/math/clamp'

export default class Color {
  constructor(r, g, b) {
    this.r = 1;
    this.g = 1;
    this.b = 1;

    if ( g === undefined && b === undefined ) {
      // r is THREE.Color, hex or string
      this.set(r);
    }

    this.setRGB(r, g, b);

    this.setHSL = (()=> {
      function hue2rgb( p, q, t ) {
        if ( t < 0 ) t += 1;
        if ( t > 1 ) t -= 1;
        if ( t < 1 / 6 ) return p + ( q - p ) * 6 * t;
        if ( t < 1 / 2 ) return q;
        if ( t < 2 / 3 ) return p + ( q - p ) * 6 * ( 2 / 3 - t );
        return p;
      }

      return function setHSL( h, s, l ) {
        // h,s,l ranges are in 0.0 - 1.0
        // h = _Math.euclideanModulo( h, 1 );
        h = ( ( h % 1 ) + h ) % h;
        s = clamp( s, 0, 1 );
        l = clamp( l, 0, 1 );

        if ( s === 0 ) {
          this.r = this.g = this.b = l;
        } else {
          var p = l <= 0.5 ? l * ( 1 + s ) : l + s - ( l * s );
          var q = ( 2 * l ) - p;
          this.r = hue2rgb( q, p, h + 1 / 3 );
          this.g = hue2rgb( q, p, h );
          this.b = hue2rgb( q, p, h - 1 / 3 );
        }
        return this;
      };
    })();
  }

  set( value ) {
    if ( value && value instanceof Color) {
      this.copy( value );

    } else if ( typeof value === 'number' ) {
      this.setHex( value );

    } else if ( typeof value === 'string' ) {
      this.setStyle( value );

    }

    return this;
  }

  setScalar( scalar ) {
    this.r = scalar;
    this.g = scalar;
    this.b = scalar;
    return this;
  }

  setHex( hex ) {
    hex = Math.floor( hex );
    this.r = ( hex >> 16 & 255 ) / 255;
    this.g = ( hex >> 8 & 255 ) / 255;
    this.b = ( hex & 255 ) / 255;
    return this;
  }

  setRGB( r, g, b ) {
    this.r = r;
    this.g = g;
    this.b = b;
    return this;
  }

  clone() {
    return new this.constructor( this.r, this.g, this.b );
  }

  copy( color ) {
    this.r = color.r;
    this.g = color.g;
    this.b = color.b;
    return this;
  }

  getHex() {
    return ( this.r * 255 ) << 16 ^ ( this.g * 255 ) << 8 ^ ( this.b * 255 ) << 0;
  }

  getHexString() {
    return ( '000000' + this.getHex().toString( 16 ) ).slice( - 6 );
  }

  getHSL( target ) {
    // h,s,l ranges are in 0.0 - 1.0
    if ( target === undefined ) {
      console.warn( 'THREE.Color: .getHSL() target is now required' );
      target = { h: 0, s: 0, l: 0 };
    }

    var r = this.r, g = this.g, b = this.b;
    var max = Math.max( r, g, b );
    var min = Math.min( r, g, b );
    var hue, saturation;
    var lightness = ( min + max ) / 2.0;

    if ( min === max ) {
      hue = 0;
      saturation = 0;

    } else {
      var delta = max - min;
      saturation = lightness <= 0.5 ? delta / ( max + min ) : delta / ( 2 - max - min );
      switch ( max ) {
        case r: hue = ( g - b ) / delta + ( g < b ? 6 : 0 ); break;
        case g: hue = ( b - r ) / delta + 2; break;
        case b: hue = ( r - g ) / delta + 4; break;
      }
      hue /= 6;
    }

    target.h = hue;
    target.s = saturation;
    target.l = lightness;
    return target;
  }

  add( color ) {
    this.r += color.r;
    this.g += color.g;
    this.b += color.b;
    return this;
  }

  addColors( color1, color2 ) {
    this.r = color1.r + color2.r;
    this.g = color1.g + color2.g;
    this.b = color1.b + color2.b;
    return this;
  }

  addScalar( s ) {
    this.r += s;
    this.g += s;
    this.b += s;
    return this;
  }

  sub( color ) {
    this.r = Math.max( 0, this.r - color.r );
    this.g = Math.max( 0, this.g - color.g );
    this.b = Math.max( 0, this.b - color.b );
    return this;
  }

  multiply( color ) {
    this.r *= color.r;
    this.g *= color.g;
    this.b *= color.b;
    return this;
  }

  multiplyScalar( s ) {
    this.r *= s;
    this.g *= s;
    this.b *= s;
    return this;
  }

  lerp( color, alpha ) {
    this.r += ( color.r - this.r ) * alpha;
    this.g += ( color.g - this.g ) * alpha;
    this.b += ( color.b - this.b ) * alpha;
    return this;
  }

  equals( c ) {
    return ( c.r === this.r ) && ( c.g === this.g ) && ( c.b === this.b );
  }

  fromArray( array, offset ) {
    if ( offset === undefined ) offset = 0;
    this.r = array[ offset ];
    this.g = array[ offset + 1 ];
    this.b = array[ offset + 2 ];
    return this;
  }

  toArray( array, offset ) {
    if ( array === undefined ) array = [];
    if ( offset === undefined ) offset = 0;
    array[ offset ] = this.r;
    array[ offset + 1 ] = this.g;
    array[ offset + 2 ] = this.b;
    return array;
  }

  toJSON() {
    return this.getHex();
  }
}
